import {
  PublicClientApplication,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";

import * as OfficeHelpers from "@microsoft/office-js-helpers";
import ActionTypes from "../constants/action_types";

import basePath from "base_path";

import createReduxPromiseAction from "utils/create_redux_promise_action";

function login(requestor, token, is360Law) {
  return requestor
    .get(`${basePath}/auth/authenticate-aad${is360Law ? "-360Law" : ""}`, {
      headers: {
        authorization: `Bearer ${token.access_token}`,
        withCredentials: true,
      },
    })
    .then(response => response.data);
}

export default function initUser(requestor) {
  return createReduxPromiseAction(
    ActionTypes.USER_AUTHENTICATE_AAD_TOKEN,
    async is360Law => {
      const {clientId, redirectUrl, scope} = getAadConfig(is360Law);
      const useNew = is360Law;

      const token = await (useNew ? getNewToken : getOldToken)(
        clientId,
        redirectUrl,
        scope,
      );
      return await login(requestor, token, is360Law);
    },
  );
}

function getAadConfig(is360Law) {
  const isProd = process.env.NODE_ENV === "production";
  const isPreprod =
    document?.location?.host === "preprod.lexicallabs.com" ||
    document?.location?.host === "preprod-api.lexicallabs.com";
  const isStaging =
    document?.location?.host === "staging.lexicallabs.com" ||
    document?.location?.host === "uat-api.lexicallabs.com";

  const data = {
    dev: {
      clientId: "7ecbb74b-b8ec-4274-a7ec-421db259c9a8",
      redirectUrl: "https://localhost:5012/word/taskpane.html",
      scope: "api://35e34d30-1766-4670-8ba0-5850d4b53e5b/data.read",
    },
    prod: {
      clientId: "7ecbb74b-b8ec-4274-a7ec-421db259c9a8",
      redirectUrl: "https://api.lexicallabs.com/word/taskpane.html",
      scope: "api://35e34d30-1766-4670-8ba0-5850d4b53e5b/data.read",
    },
    staging: {
      clientId: "67db3f82-6819-443b-9b8f-91c9489c5521",
      redirectUrl: "https://uat-api.lexicallabs.com/word/taskpane.html",
      scope: "api://67db3f82-6819-443b-9b8f-91c9489c5521/data.read",
    },
    preprod: {
      clientId: "67db3f82-6819-443b-9b8f-91c9489c5521",
      redirectUrl: "https://preprod-api.lexicallabs.com/word/taskpane.html",
      scope: "api://67db3f82-6819-443b-9b8f-91c9489c5521/data.read",
    },
    "360Law": {
      clientId: "4e376bc8-886a-4b7c-be83-9d4443d7192b",
      redirectUrl: "https://api.lexicallabs.com/word/taskpane-360Law.html",
      scope: "api://4e376bc8-886a-4b7c-be83-9d4443d7192b/data.read",
    },
  };
  const key = is360Law
    ? "360Law"
    : isPreprod
    ? "preprod"
    : isStaging
    ? "staging"
    : isProd
    ? "prod"
    : "dev";
  return data[key];
}

async function getNewToken(clientId, redirectUrl, scope) {
  const msalConfig = {
    auth: {
      clientId,
      authority: "https://login.microsoftonline.com/common", // or your tenant ID
      redirectUrl,
    },
    cache: {
      cacheLocation: "sessionStorage",
      storeAuthStateInCookie: false,
    },
  };

  const pca = new PublicClientApplication(msalConfig);

  await pca.initialize();

  const scopesArray = scope.split(" ");

  let accessToken;
  try {
    const accounts = pca.getAllAccounts();

    if (accounts.length > 0) {
      // silent if we’ve already got an account
      const silentResult = await pca.acquireTokenSilent({
        account: accounts[0],
        scopes: scopesArray,
      });
      accessToken = silentResult.accessToken;
    } else {
      // first-time interactive login
      const loginResult = await pca.loginPopup({scopes: scopesArray});
      accessToken = loginResult.accessToken;
    }
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      const popupResult = await pca.acquireTokenPopup({
        scopes: scopesArray,
      });
      accessToken = popupResult.accessToken;
    } else {
      throw err;
    }
  }
  const token = {access_token: accessToken};
  return token;
}

async function getOldToken(clientId, redirectUrl, scope) {
  const authenticator = new OfficeHelpers.Authenticator();
  authenticator.endpoints.registerMicrosoftAuth(clientId, {
    redirectUrl,
    scope,
  });
  let token = authenticator.tokens.get("Microsoft");

  if (!token) {
    const newToken = await authenticator.authenticate(
      OfficeHelpers.DefaultEndpoints.Microsoft,
      true,
    );
    token = newToken;
  }
  return token;
}
